import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';

import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet } from '../../store/fetch';
import { codeToColorCode } from '../../utils/conditionHelper';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { InputRadioButtons, SmallSelect } from '../elements';
import { Spinner } from '../spinners';
import { ComponentSetStatePromised } from '../utils/ComponentSetStatePromised';
import { DisplayMessage } from '../utils/displayMessage';
import { GenericMap } from '../utils/GenericMap';

interface IMaintenanceMapProps extends ITranslateProps, IWithReactRouterProps {
    isPdokMapLayerAvailable: boolean;
}

interface IMaintenanceMeasure {
    id: string;
    survey: {
        object: {
            id: string;
            name: string;
            shapeGeoJson?: string;
            latitude?: number;
            longitude?: number;
        };
    };
    scoreSummary: {
        maxScore: number;
        conditionScore?: number;
        careScore?: number;
        riskConstruction?: number;
        riskDefect?: number;
        riskFmeca?: number;
    };
}

interface IMaintenanceMeasuresMapState {
    isLoading: boolean;
    noDataAvailable: boolean;
    items: IMaintenanceMeasure[];
    totalItems: number;
    latitude: number;
    longitude: number;
    zoom: number;
    currentIndex: number;
    currentTitle: string;
    currentSurveyDate: Date;
    currentId: string;

    condition?: string;
    conditionCareScore?: string;
    searchText?: string;
    riskDefect?: string;
    riskConstruction?: string;
    riskFmeca?: string;

    visibleScore: 'riskDefect' | 'fmecaRisk' | 'riskConstruction' | 'condition' | 'care';
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
    };
}

class MaintenanceMeasuresMapComponent extends ComponentSetStatePromised<IMaintenanceMapProps, IMaintenanceMeasuresMapState> {
    constructor(props: IMaintenanceMapProps) {
        super(props);

        this.state = {
            isLoading: false,
            noDataAvailable: false,
            items: [],
            totalItems: 0,
            latitude: 51.808504,
            longitude: 5.703935,
            zoom: 2,
            currentIndex: 0,
            currentTitle: '',
            currentSurveyDate: new Date(),
            currentId: '',
            visibleScore: 'condition',
        };
    }

    public override async componentDidMount() {
        await this.search();
    }

    public override render() {
        const defaultZoom = 12;

        return (
            <span>
                <header id="topbar" className="ph10">
                    <div className="topbar-left">
                        <ol className="breadcrumb">
                            <li className="crumb-active">
                                <span className="fa fa-camera pr10"></span>
                                <Link to="/MaintenanceMeasures">{this.props.t('maintenanceMeasure.maintenanceMeasures')}</Link>
                            </li>
                        </ol>
                    </div>
                </header>

                <section id="content" className="pn posr">
                    <div style={{ position: 'relative', height: '100%' }}>
                        <form method="post" action="/" id="filter">
                            <div className="map-menu admin-form">
                                <div className="mt10">
                                    <SmallSelect
                                        name="riskDefect"
                                        value={this.state.riskDefect}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        topLabel={this.props.t('survey.riskDefect')}
                                        placeholder={this.props.t('common.selectItem', this.props.t('survey.riskDefect'))}
                                        options={[
                                            { value: '4', text: 'Direct herstellen' },
                                            { value: '3', text: 'Binnen 2 jaar herstellen' },
                                            { value: '2', text: 'Binnen 5 jaar herstellen' },
                                            { value: '1', text: 'Herstellen na 5 jaar' },
                                        ]}
                                    />
                                </div>
                                {isCustomerVersion('zxy') && (
                                    <div className="mt10">
                                        <SmallSelect
                                            name="riskFmeca"
                                            value={this.state.riskFmeca}
                                            handleChange={this.handleChange.bind(this)}
                                            disabled={this.state.isLoading}
                                            placeholder={this.props.t('common.selectItem', this.props.t('survey.fmecaRisk'))}
                                            topLabel={this.props.t('survey.fmecaRisk')}
                                            options={[
                                                { value: '1', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.1')) },
                                                { value: '2', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.2')) },
                                                { value: '3', text: this.props.t('condition.orMore', this.props.t('fmecaRisk.3')) },
                                                { value: '4', text: this.props.t('fmecaRisk.4') },
                                            ]}
                                        />
                                    </div>
                                )}
                                {isCustomerVersion('zxy') && (
                                    <div className="mt10">
                                        <SmallSelect
                                            name="riskConstruction"
                                            value={this.state.riskConstruction}
                                            handleChange={this.handleChange.bind(this)}
                                            disabled={this.state.isLoading}
                                            placeholder={this.props.t('common.selectItem', this.props.t('survey.riskConstruction'))}
                                            topLabel={this.props.t('survey.riskConstruction')}
                                            options={[
                                                { value: '1', text: this.props.t('condition.orMore', this.props.t('cra.riskScore1')) },
                                                { value: '2', text: this.props.t('condition.orMore', this.props.t('cra.riskScore2')) },
                                                { value: '3', text: this.props.t('condition.orMore', this.props.t('cra.riskScore3')) },
                                                { value: '4', text: this.props.t('cra.riskScore4') },
                                            ]}
                                        />
                                    </div>
                                )}
                                <div className="mt10">
                                    <SmallSelect
                                        name="condition"
                                        value={this.state.condition}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        placeholder={this.props.t('common.selectItem', this.props.t('common.conditions'))}
                                        topLabel={this.props.t('common.conditions')}
                                        options={[
                                            { value: '2', text: this.props.t('condition.orLess', this.props.t('condition.good')) },
                                            { value: '3', text: this.props.t('condition.orLess', this.props.t('condition.reasonable')) },
                                            { value: '4', text: this.props.t('condition.orLess', this.props.t('condition.moderate')) },
                                            { value: '5', text: this.props.t('condition.orLess', this.props.t('condition.poor')) },
                                            { value: '6', text: this.props.t('condition.bad') },
                                        ]}
                                    />
                                </div>
                                {/* <div className="mt10">
                                    <SmallSelect
                                        name="conditionCareScore"
                                        value={this.state.conditionCareScore}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        placeholder={this.props.t('common.selectItem', this.props.t('common.care'))}
                                        topLabel={this.props.t('common.care')}
                                        options={[
                                            { value: '2', text: this.props.t('condition.orLess', this.props.t('condition.good')) },
                                            { value: '3', text: this.props.t('condition.orLess', this.props.t('condition.reasonable')) },
                                            { value: '4', text: this.props.t('condition.orLess', this.props.t('condition.moderate')) },
                                            { value: '5', text: this.props.t('condition.orLess', this.props.t('condition.poor')) },
                                            { value: '6', text: this.props.t('condition.bad') },
                                        ]}
                                    />
                                </div> */}
                                {/* <div className="mt10">
                                    <InputSearchText
                                        name="searchText"
                                        value={this.state.searchText}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        placeholder={this.props.t('common.description')}
                                    />
                                </div>{' '} */}
                                <div className="mt10 ml5">
                                    <InputRadioButtons
                                        name="visibleScore"
                                        value={this.state.visibleScore}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        topLabel={this.props.t('maintenanceMeasure.showScore')}
                                        options={[
                                            ...[
                                                { value: 'riskDefect', text: this.props.t('survey.riskDefect') },
                                                { value: 'condition', text: this.props.t('common.condition') },
                                                // { value: 'care', text: this.props.t('common.care') },
                                            ],
                                            ...(isCustomerVersion('zxy') ? [{ value: 'fmecaRisk', text: this.props.t('survey.fmecaRisk') }] : []),
                                            ...(isCustomerVersion('zxy')
                                                ? [{ value: 'riskConstruction', text: this.props.t('survey.riskConstruction') }]
                                                : []),
                                        ]}
                                    />
                                </div>
                            </div>
                        </form>
                        {this.state.isLoading && !this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <Spinner />
                            </div>
                        )}

                        {this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <DisplayMessage message={this.props.t('survey.noItemsOnMap')} messageType="noData" />
                            </div>
                        )}

                        {this.state.totalItems > 200 && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <DisplayMessage
                                    message={this.props.t('maintenanceMeasure.moreThan200Items', this.state.totalItems.toString())}
                                    messageType="general"
                                />
                            </div>
                        )}

                        <GenericMap
                            mapLoaded={this.mapLoaded.bind(this)}
                            zoom={defaultZoom}
                            center={{ lat: this.state.latitude, lng: this.state.longitude }}
                            isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                            isDroneNoFlyMapNlAvailable={false}
                            markers={this.state.items.map((maintenanceMeasurement) => ({
                                key: maintenanceMeasurement.id,
                                position: {
                                    lat: maintenanceMeasurement.survey.object.latitude ?? 0,
                                    lng: maintenanceMeasurement.survey.object.longitude ?? 0,
                                },
                                title: maintenanceMeasurement.survey.object.name,
                                onClick: this.handleMarkerRightclick.bind(this, maintenanceMeasurement),
                                color: codeToColorCode(this.getScore(maintenanceMeasurement), undefined),
                                shapeGeoJson: maintenanceMeasurement.survey.object.shapeGeoJson
                                    ? JSON.parse(maintenanceMeasurement.survey.object.shapeGeoJson)
                                    : undefined,
                            }))}
                        />
                    </div>
                </section>
            </span>
        );
    }

    private getScore(maintenanceMeasurement: IMaintenanceMeasure) {
        const maintenanceMeasurementsWithSameObject = this.state.items.filter((i) => i.survey.object.id === maintenanceMeasurement.survey.object.id);

        let scores: (number | undefined)[];

        switch (this.state.visibleScore) {
            case 'care': {
                scores = maintenanceMeasurementsWithSameObject.map((i) => i.scoreSummary.careScore);
                break;
            }
            case 'condition': {
                scores = maintenanceMeasurementsWithSameObject.map((i) => i.scoreSummary.conditionScore);
                break;
            }
            case 'fmecaRisk': {
                scores = maintenanceMeasurementsWithSameObject.map((i) => i.scoreSummary.riskFmeca);
                break;
            }
            case 'riskConstruction': {
                scores = maintenanceMeasurementsWithSameObject.map((i) => i.scoreSummary.riskConstruction);
                break;
            }
            case 'riskDefect': {
                scores = maintenanceMeasurementsWithSameObject.map((i) => i.scoreSummary.riskDefect);
                break;
            }
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown visibleScore '${this.state.visibleScore}'`);
        }

        const scoresWithoutUndefined = scores.filter((i) => i !== undefined);

        if (scoresWithoutUndefined.length === 0) {
            return undefined;
        }

        return Math.max(...scoresWithoutUndefined).toString();
    }

    private async mapLoaded() {
        await this.setStatePromised({ isLoading: false });
    }

    private handleMarkerRightclick(maintenanceMeasurement: any) {
        this.props.navigate(`/objects/${maintenanceMeasurement.survey.object.id}`);
    }

    private async handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        await this.setStatePromised(newState);

        if (
            stateKey === 'riskDefect' &&
            this.state.riskFmeca === undefined &&
            this.state.riskConstruction === undefined &&
            this.state.condition === undefined &&
            this.state.conditionCareScore === undefined
        ) {
            await this.setStatePromised({ visibleScore: 'riskDefect' });
        }
        if (
            stateKey === 'riskFmeca' &&
            this.state.riskDefect === undefined &&
            this.state.riskConstruction === undefined &&
            this.state.condition === undefined &&
            this.state.conditionCareScore === undefined
        ) {
            await this.setStatePromised({ visibleScore: 'fmecaRisk' });
        }
        if (
            stateKey === 'riskConstruction' &&
            this.state.riskDefect === undefined &&
            this.state.riskFmeca === undefined &&
            this.state.condition === undefined &&
            this.state.conditionCareScore === undefined
        ) {
            await this.setStatePromised({ visibleScore: 'riskConstruction' });
        }
        if (
            stateKey === 'condition' &&
            this.state.riskDefect === undefined &&
            this.state.riskFmeca === undefined &&
            this.state.riskConstruction === undefined &&
            this.state.conditionCareScore === undefined
        ) {
            await this.setStatePromised({ visibleScore: 'condition' });
        }
        if (
            stateKey === 'conditionCareScore' &&
            this.state.riskDefect === undefined &&
            this.state.riskFmeca === undefined &&
            this.state.riskConstruction === undefined &&
            this.state.condition === undefined
        ) {
            await this.setStatePromised({ visibleScore: 'care' });
        }

        await this.search();
    }

    private async search() {
        await this.setStatePromised({ isLoading: true, noDataAvailable: false });
        const result = await fetchGet('/api/maintenanceMeasures', {
            limit: '200',
            offset: '0',
            includeObject: 'true',
            condition: this.state.condition ? this.state.condition : undefined,
            conditionCareScore: this.state.conditionCareScore ? this.state.conditionCareScore : undefined,
            searchText: this.state.searchText ? this.state.searchText : undefined,
            riskconstruction: this.state.riskConstruction ? this.state.riskConstruction : undefined,
            riskdefect: this.state.riskDefect ? this.state.riskDefect : undefined,
            riskfmeca: this.state.riskFmeca ? this.state.riskFmeca : undefined,
        });

        if (result.json.items.total === 0) {
            await this.setStatePromised({ noDataAvailable: true });
        } else {
            await this.setStatePromised({ items: result.json.items, totalItems: result.json.total });
            if (this.state.items[0] && !this.state.currentId) {
                this.setState({
                    // latitude: this.state.items[0].survey.object.latitude,
                    // longitude: this.state.items[0].survey.object.longitude,
                    currentTitle: this.state.items[0].survey.object.name,
                    currentIndex: 0,
                    currentId: this.state.items[0].id,
                });
            }
        }
        await this.setStatePromised({ isLoading: false });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MaintenanceMeasuresMap = translate()(
    connect(mapStateToProps, undefined, undefined, { forwardRef: true })(MaintenanceMeasuresMapComponent as any) as any,
);
